import { faCloudArrowUp } from "@fortawesome/pro-duotone-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Link, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { getFormattedFileSize } from "app/tools/file"
import { IGeoTargetsFileImport } from "form/model"
import {
  getGeoTargetsFileImport,
  selectGeoTargetsFileImportFiles,
  setGeoTargetsFileImportError
} from "form/tools/geo-targets-file-import"
import { MAX_MEDIA_FILE_UPLOAD_SIZE_IN_BYTES } from "media/model"
import { messageSVC } from "message/service"
import React from "react"
import { FileDrop } from "react-file-drop"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"

interface IGeoTargetsImportFileUploadProps {
  containerHeight: number
  fileImport: IGeoTargetsFileImport
}

const INPUT_ID = "geo-targets-upload-input"

const GeoTargetsImportFileUploadFn = React.memo(
  ({ containerHeight, fileImport: { error, isSaving } }: IGeoTargetsImportFileUploadProps): JSX.Element => {
    const onFileSelection = (files: FileList | null) => {
      if (isSaving) {
        return
      }
      if (error) {
        setGeoTargetsFileImportError("")
      }
      if (files) {
        if (files[0].type !== "text/csv") {
          messageSVC.error(translate("campaign.geo-targets.file.import.error.extension"))
          return
        }
      }
      selectGeoTargetsFileImportFiles(files)
    }
    return (
      <FlexBox
        sx={{
          "width": "50%",
          "maxWidth": "50%",
          "minWidth": "50%",
          "height": `${Math.min(containerHeight, 200)}px`,
          "border": theme => `1px dashed ${theme.palette.text.secondary}`,
          "backgroundColor": theme => theme.palette.background.paper,
          "color": theme => theme.palette.text.primary2,
          "cursor": "pointer",
          "& > *": {
            maxWidth: "19rem"
          },
          "& .file-drop": {
            width: "100%",
            minWidth: "100%",
            maxWidth: "100%",
            height: "100%",
            minHeight: "100%",
            maxHeight: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }
        }}
        onClick={() => document.getElementById(INPUT_ID)?.click()}>
        <FileDrop onDrop={files => onFileSelection(files)} className="file-drop">
          <FlexBox
            vertical
            sx={{
              "& > *": {
                maxWidth: "19rem"
              }
            }}>
            <FontAwesomeIcon icon={faCloudArrowUp} className="drop-icon" size="2x" opacity=".5" />
            <Typography fontSize="1rem" textAlign="center">
              {translate("label.drop.file")}
            </Typography>
            <FlexBox gap=".3rem">
              <Typography fontSize="1rem">{translate("label.or")}</Typography>
              <div>
                <input
                  id={INPUT_ID}
                  style={{ display: "none" }}
                  type="file"
                  accept=".csv"
                  onChange={event => onFileSelection(event.target.files)}
                />
                <Link component="button" variant="body2" fontSize="1rem" disabled={isSaving}>
                  {translate("browse")}
                </Link>
              </div>
            </FlexBox>
            <Typography color="textSecondary" fontSize=".8rem" textAlign="center">
              {translate("label.file.max.size", getFormattedFileSize(MAX_MEDIA_FILE_UPLOAD_SIZE_IN_BYTES, 0))}
            </Typography>
          </FlexBox>
        </FileDrop>
      </FlexBox>
    )
  }
)

export const GeoTargetsImportFileUpload = connect(
  (state: IRootState): IGeoTargetsImportFileUploadProps => ({
    containerHeight: state.ui.container.height,
    fileImport: getGeoTargetsFileImport(state)
  })
)(GeoTargetsImportFileUploadFn)
